/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-param-reassign */

import React, { useEffect, useState } from 'react';
import { bool, shape, array, node, oneOf } from 'prop-types';
import styled from 'styled-components';
import FlexWrapper from 'TMComponents/FlexWrapper';
import Img from 'TMComponents/ImageDynamic';
import { breakpoints } from 'TMConstants';
import imageProps from 'TMPropTypes/imageProps';
import Content from 'TMComponents/Content';
import debounce from 'TMUtils/debounce';
import cardBackgroundOption from 'TMComponents/GlobalStyle/cardBackgroundStyles';
import parseCommonOptions from 'TMComponents/ComponentMapper/parseCommonOptions';

const Background = styled.div`
    position: relative;
    height: 100%;

    padding: 20px;
    ${({ reducedPadding }) => reducedPadding && `padding: 0px;`}

    ${({ backgroundVariant }) =>
        backgroundVariant &&
        `
         background-color: ${backgroundVariant};
    `};
`;

const Sections = styled(FlexWrapper)`
    gap: 20px;
    flex-direction: column;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        flex-direction: row;
    }

    justify-content: center;
    align-items: center;
    align-content: center;
    justify-items: center;
    max-width: 1400px;
    box-sizing: border-box;

    overflow: clip;
    margin-left: auto;
    margin-right: auto;

    ${({ theme, frameVariant }) =>
        frameVariant === 'card' &&
        `
        border-radius: 32px;
        box-shadow: ${theme.colorSystem.shadows[10]};
        `}

    ${({ frameVariant }) =>
        frameVariant === 'card' && cardBackgroundOption}
`;

const ContentSection = styled(FlexWrapper)`
    ${({ hasImage }) => hasImage && `flex: 1.8;`}
    padding: 32px 0;
    ${({ reducedPadding }) => reducedPadding && `padding: 0px;`}
    margin: auto;
    @media (min-width: ${breakpoints.tabletDesktop}px) {
        padding: 32px 80px;
    }

    @media (min-width: ${breakpoints.mobileTablet}px) and (max-width: ${breakpoints.tabletDesktop}px) {
        ${({ hasImage }) => hasImage && `flex: 50%;`}
    }
`;

const ImageSection = styled(FlexWrapper)`
    align-items: center;
    justify-content: center;
    overflow: hidden;
    max-width: 640px;
    @media (min-width: ${breakpoints.mobileTablet}px) and (max-width: ${breakpoints.tabletDesktop}px) {
        align-self: flex-end;
        flex: 50%;
    }
`;

const Image = styled(Img)`
    bottom: 0;
    flex: 1;
`;

const Hero = ({
    desktopImage,
    mobileImage,
    commonOptions,
    frameVariant,
    content,
    children,
    reducedPadding
}) => {
    const [image, setImage] = useState(mobileImage);
    const [viewportWidth, setViewportWidth] = useState(null);

    useEffect(() => {
        if (!viewportWidth) setViewportWidth(window.innerWidth);
        if (viewportWidth < breakpoints.mobileTablet - 100) {
            setImage(mobileImage);
        } else {
            setImage(desktopImage);
        }
    }, [viewportWidth]);

    useEffect(() => {
        window.addEventListener(
            'resize',
            debounce(() => setViewportWidth(window.innerWidth), 100)
        );
        /* istanbul ignore next */
        return () => {
            window.removeEventListener(
                'resize',
                debounce(() => setViewportWidth(window.innerWidth), 100)
            );
        };
    }, []);

    const headerContent = content.map((cmsComponent) => {
        if (
            cmsComponent.__typename === 'DatoCmsText' ||
            cmsComponent.__typename === 'DatoCmsTextStructured'
        ) {
            cmsComponent.allowH1 = true;
        }
        return cmsComponent;
    });

    const isDesktop = viewportWidth >= breakpoints.mobileTablet;

    const common = parseCommonOptions(commonOptions);

    return (
        <Background
            backgroundVariant={common?.backgroundVariant}
            reducedPadding={reducedPadding}>
            <Sections
                frameVariant={frameVariant}
                cardBackgroundColor={common?.cardBackgroundColor}>
                <ContentSection
                    align="center"
                    hasImage={!!image}
                    reducedPadding={reducedPadding}>
                    <Content
                        backgroundVariant={common?.backgroundVariant}
                        content={headerContent}
                        leftAlign={!!image}>
                        {children}
                    </Content>
                </ContentSection>
                {image && (
                    <ImageSection>
                        <Image
                            imageData={image}
                            loading="eager"
                            objectPosition="30% 30%"
                        />
                    </ImageSection>
                )}
            </Sections>
        </Background>
    );
};

Hero.propTypes = {
    children: node,
    desktopImage: shape(imageProps),
    mobileImage: shape(imageProps),
    content: array.isRequired,
    commonOptions: array,
    frameVariant: oneOf(['plain', 'card']),
    reducedPadding: bool
};

export default Hero;
